import { ThemeColorName } from '@examdojo/styles';
import type { Board, BoardAttributes } from 'jsxgraph';

import type { TransitionProfile } from '../../model/widget-action.model';
import type { BehaviorConfig } from '../../model/widget-behavior.model';
import type { BoundingBoxConfig } from './coordinate-system';
import type { ThemeColorWithShade } from './elements/theme/theme.model';

// =============================================================================
// JSXGRAPH LIBRARY TYPES
// =============================================================================

export type JsxgraphBoard = Board;
export type JsxgraphStatic = typeof import('jsxgraph');
export type JsxgraphBoardOptions = Partial<BoardAttributes>;

// =============================================================================
// THEME (colors, typography, spacing - raw values only)
// =============================================================================

/**
 * Theme color palette for JSXGraph elements.
 */
export interface ThemeColors {
  /** Color for axis lines */
  axis: string;
  /** Color for grid lines */
  grid: string;
  /** Background color for the board container */
  background: string;
  /** Border color for the board container */
  border: string;
  /** Secondary text color (for labels, tick marks) */
  textSecondary: string;
  /** Array of feature colors for data visualization */
  feature: string[];
  /** Shading colors keyed by theme color name */
  shading: Record<ThemeColorName, string>;
}

/**
 * Theme typography settings.
 */
export interface ThemeTypography {
  fontFamily: string;
  fontSize: {
    base: number;
    small: number;
    label: number;
  };
  fontWeight: {
    normal: number;
    medium: number;
    bold: number;
  };
}

/**
 * Theme spacing and visual settings.
 */
export interface ThemeSpacing {
  borderRadius: string;
  labelPadding: string;
  labelOffset: [number, number];
}

/**
 * Label color helpers for different label variants.
 */
export interface LabelColors {
  white: string;
  bgLight: (color: ThemeColorName) => string;
  textDark: (color: ThemeColorName) => string;
  textDarker: (color: ThemeColorName) => string;
}

/**
 * Complete theme configuration for JSXGraph.
 * Contains only raw values - no JSXGraph API options.
 */
export interface JsxgraphTheme {
  colors: ThemeColors;
  typography: ThemeTypography;
  spacing: ThemeSpacing;
  labelColors: LabelColors;
}

// =============================================================================
// FEATURE CONFIGURATION (high-level widget config)
// =============================================================================

/**
 * High-level axes feature configuration.
 * Controls what axis features to display.
 */
export interface AxesFeatureConfig {
  enabled: boolean;
  xAxis?: boolean;
  yAxis?: boolean;
  ticks?: boolean;
  labels?: boolean;
}

/**
 * Display configuration for JSXGraph widgets.
 */
export interface DisplayConfig {
  /** Show/hide grid */
  grid?: boolean;
  axes?: AxesFeatureConfig | boolean;
  boundingbox?: BoundingBoxConfig;
}

/**
 * Interaction feature configuration.
 */
export interface InteractionFeatureConfig {
  enabled: boolean;
  draggable?: boolean;
  zoomable?: boolean;
  pannable?: boolean;
  padding?: number;
}

/**
 * JSXGraph-specific features that go inside behavior.features.
 */
export interface JsxgraphFeatures extends Record<string, unknown> {
  interaction?: InteractionFeatureConfig | boolean;
}

export type JsxgraphActionType =
  | 'FOCUS_REGION'
  | 'TRANSFORM_VIEW'
  | 'PATCH_DISPLAY'
  | 'HIGHLIGHT'
  | 'RESET_VIEW'
  | 'UPDATE_PARAM';

/**
 * JSXGraph-specific behavior configuration.
 */
export type JsxgraphBehaviorConfig<
  ActionTarget extends string = string,
  ActionType extends string | never = never,
> = BehaviorConfig<ActionTarget, ActionType | JsxgraphActionType, JsxgraphFeatures>;

// =============================================================================
// CANVAS CONFIG (visual container properties, not coordinate math)
// =============================================================================

export interface BorderConfig {
  /** Whether the border is visible. @default true */
  visible?: boolean;
  /** Border color as theme token (e.g. 'neutral-200', 'sky-300'). Resolved as var(--<color>). @default 'neutral-200' */
  color?: ThemeColorWithShade;
}

export function resolveBorderConfig(config: BorderConfig | undefined): BorderConfig {
  if (config === undefined) {
    return { visible: true, color: 'neutral-200' };
  }
  return { visible: config.visible !== false, color: config.color ?? 'neutral-200' };
}

/** Visual properties of the canvas container (background color, border). */
export interface CanvasConfig {
  /** Background color using CSS custom property names (e.g. 'neutral-50', 'sky-50'). Resolved as var(--<color>). */
  color?: ThemeColorWithShade;
  /** Border configuration. Omit for default border (1px solid neutral-200). */
  border?: BorderConfig;
}

// =============================================================================
// STYLING OVERRIDES (optional customization)
// =============================================================================

/**
 * Optional line style overrides.
 */
export interface LineStyleOverrides {
  colorName?: ThemeColorName;
  strokeColor?: string;
  strokeWidth?: number;
  dash?: number;
}

// =============================================================================
// DEFAULTS
// =============================================================================

export const DEFAULT_AXES_CONFIG: AxesFeatureConfig = {
  enabled: true,
  xAxis: true,
  yAxis: true,
  ticks: true,
  labels: true,
};

export const DEFAULT_DISPLAY_CONFIG: DisplayConfig = {
  grid: false,
  axes: DEFAULT_AXES_CONFIG,
  boundingbox: { xStart: -10, xEnd: 10, yStart: -10, yEnd: 10 },
};

export const DEFAULT_INTERACTION_CONFIG: InteractionFeatureConfig = {
  enabled: true,
  draggable: true,
  zoomable: true,
  pannable: true,
};

export const DEFAULT_JSXGRAPH_FEATURES: JsxgraphFeatures = {
  interaction: DEFAULT_INTERACTION_CONFIG,
};

export const DEFAULT_JSXGRAPH_BEHAVIOR_CONFIG: JsxgraphBehaviorConfig = {
  features: DEFAULT_JSXGRAPH_FEATURES,
};

export const DEFAULT_BOARD_OPTIONS: JsxgraphBoardOptions = {
  showCopyright: false,
  showNavigation: false,
  axis: false,
  grid: false,
  keepAspectRatio: false,
  boundingbox: [-5, 5, 5, -5],
  showInfobox: false,
};

// =============================================================================
// DEFAULT TRANSITIONS
// =============================================================================

export const DEFAULT_TRANSITIONS: Record<string, TransitionProfile> = {
  instant: { duration: 0, easing: 'linear', type: 'none' },
  fast: { duration: 150, easing: 'easeOut', type: 'numeric' },
  default: { duration: 400, easing: 'easeInOut', type: 'numeric' },
  slow: { duration: 800, easing: 'easeInOut', type: 'numeric' },
  camera: { duration: 400, easing: 'easeInOut', type: 'camera' },
  bounce: { duration: 600, easing: 'bounce', type: 'numeric' },
};

// =============================================================================
// UTILITIES
// =============================================================================

export function normalizeConfig<T extends { enabled: boolean }>(config: T | boolean | undefined, defaults: T): T {
  if (config === undefined || config === false) {
    return { ...defaults, enabled: false };
  }
  if (config === true) {
    return { ...defaults, enabled: true };
  }
  return { ...defaults, ...config };
}
